import { useCallback, useState } from "react";
import { updatePhoto } from "../services/photoService";
import { uploadPhotoVersion, validateFile } from "../services/storageService";
import type { FileUploadObj, FileUploadProgress } from "../types";
import type { Photo } from "../types/api";
import {
  generateFileId,
  uploadWithConcurrency,
  validateAndFilterFiles,
  formatErrorMessage,
} from "../utils/uploadUtils";

export interface UseProductReadyUploadReturn {
  files: FileUploadObj[];
  matchedPhotos: Map<string, Photo>;
  unmatchedFiles: FileUploadObj[];
  uploading: boolean;
  progress: Record<string, FileUploadProgress>;
  overallProgress: { current: number; total: number };
  error: string;
  success: string;
  onDrop: (acceptedFiles: File[]) => void;
  matchFile: (fileId: string, photoId: string) => void;
  removeFile: (fileId: string) => void;
  uploadAll: () => Promise<void>;
  setError: (error: string) => void;
  setSuccess: (success: string) => void;
}

export const useProductReadyUpload = (
  photos: Photo[],
  campusId: string,
  childId: string
): UseProductReadyUploadReturn => {
  const [files, setFiles] = useState<FileUploadObj[]>([]);
  const [matchedPhotos, setMatchedPhotos] = useState<Map<string, Photo>>(
    new Map()
  );
  const [uploading, setUploading] = useState<boolean>(false);
  const [progress, setProgress] = useState<Record<string, FileUploadProgress>>(
    {}
  );
  const [overallProgress, setOverallProgress] = useState<{
    current: number;
    total: number;
  }>({ current: 0, total: 0 });
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  /**
   * Extract index number from filename
   * Matches patterns like: "1.jpg", "photo_1.jpg", "IMG_001.jpg", "1_final.jpg"
   * Returns 1-based index number, or null if no match
   */
  const extractIndexFromFilename = (filename: string): number | null => {
    // Remove extension
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");

    // Try different patterns to extract index number
    const patterns = [
      /^(\d+)$/i, // "1", "2", "3"
      /^(\d+)_/i, // "1_final", "2_photo"
      /_(\d+)$/i, // "photo_1", "img_2"
      /_(\d+)_/i, // "photo_1_final", "img_2_edit"
      /^photo[\s_-]?(\d+)/i, // "photo1", "photo_1", "photo-1"
      /^img[\s_-]?(\d+)/i, // "img1", "IMG_001"
      /(\d+)/, // Fallback: first number found
    ];

    for (const pattern of patterns) {
      const match = nameWithoutExt.match(pattern);
      if (match && match[1]) {
        const index = parseInt(match[1], 10);
        if (!isNaN(index) && index > 0) {
          return index; // Return 1-based index
        }
      }
    }

    return null;
  };

  /**
   * Auto-match file to photo by index
   * If filename is "1.jpg", match to photos[0]
   * If filename is "2.jpg", match to photos[1]
   */
  const findMatchingPhoto = (filename: string): Photo | null => {
    const index = extractIndexFromFilename(filename);
    if (index === null) return null;

    // Convert 1-based index to 0-based array index
    const photoIndex = index - 1;

    // Check if index is within bounds
    if (photoIndex >= 0 && photoIndex < photos.length) {
      return photos[photoIndex];
    }

    return null;
  };

  // Handle file drop
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const validFiles = validateAndFilterFiles(acceptedFiles, validateFile, setError);

      const newFiles: FileUploadObj[] = validFiles.map((file) => ({
        file,
        id: generateFileId(),
        preview: URL.createObjectURL(file),
        uploading: false,
        uploaded: false,
      }));

      // Auto-match files to photos by index
      const newMatches = new Map(matchedPhotos);
      newFiles.forEach((fileObj) => {
        const matchedPhoto = findMatchingPhoto(fileObj.file.name);
        if (matchedPhoto) {
          newMatches.set(fileObj.id, matchedPhoto);
          console.log(
            `Auto-matched "${fileObj.file.name}" to Photo #${
              photos.indexOf(matchedPhoto) + 1
            }`
          );
        }
      });

      setFiles((prev) => [...prev, ...newFiles]);
      setMatchedPhotos(newMatches);
    },
    [matchedPhotos, photos]
  );

  // Manual file matching
  const matchFile = useCallback(
    (fileId: string, photoId: string) => {
      const photo = photos.find((p) => p.id === photoId);
      if (photo) {
        setMatchedPhotos((prev) => {
          const newMap = new Map(prev);
          newMap.set(fileId, photo);
          return newMap;
        });
      }
    },
    [photos]
  );

  // Remove file
  const removeFile = useCallback((fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
    setMatchedPhotos((prev) => {
      const newMap = new Map(prev);
      newMap.delete(fileId);
      return newMap;
    });
  }, []);

  // Get unmatched files
  const unmatchedFiles = files.filter((file) => !matchedPhotos.has(file.id));

  /**
   * Upload a single file and update its progress
   */
  const uploadSingleFile = async (fileObj: FileUploadObj): Promise<boolean> => {
    const photo = matchedPhotos.get(fileObj.id);
    if (!photo) return false;

    try {
      // Update progress
      setProgress((prev) => ({
        ...prev,
        [fileObj.id]: { uploading: true, progress: 0 },
      }));

      // Upload final version to Firebase Storage with progress tracking
      const uploadResult = await uploadPhotoVersion(
        fileObj.file,
        campusId,
        childId,
        photo.id,
        "final",
        (progress) => {
          // Update progress 0-100%
          setProgress((prev) => ({
            ...prev,
            [fileObj.id]: { uploading: true, progress },
          }));
        }
      );

      // Update Photo document with finalUrl
      await updatePhoto(photo.id, {
        finalUrl: uploadResult.downloadURL,
      });

      // Mark as uploaded
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileObj.id
            ? {
                ...f,
                uploaded: true,
                downloadURL: uploadResult.downloadURL,
                storagePath: uploadResult.storagePath,
              }
            : f
        )
      );

      // Update progress to completed
      setProgress((prev) => ({
        ...prev,
        [fileObj.id]: { uploading: false, progress: 100, completed: true },
      }));

      return true;
    } catch (err) {
      console.error("Upload failed for file:", fileObj.file.name, err);
      setProgress((prev) => ({
        ...prev,
        [fileObj.id]: { uploading: false, error: true },
      }));
      return false;
    }
  };

  // Upload all matched files in chunks
  const uploadAll = useCallback(
    async (): Promise<void> => {
      if (files.length === 0) {
        setError("Please select files to upload");
        return;
      }

      const matchedFiles = files.filter((file) => matchedPhotos.has(file.id));

      if (matchedFiles.length === 0) {
        setError("No matched files to upload. Please match files to photos.");
        return;
      }

      setUploading(true);
      setError("");
      setSuccess("");
      setProgress({});
      setOverallProgress({ current: 0, total: matchedFiles.length });

      try {
        // Split files that need uploading from already uploaded ones
        const filesToUpload = matchedFiles.filter((f) => !f.uploaded);
        const alreadyUploaded = matchedFiles.filter((f) => f.uploaded);

        // Count already uploaded files as success
        const preUploadedCount = alreadyUploaded.length;

        // Use concurrency utility for uploading with progress tracking
        const { successCount: newSuccessCount, errorCount } =
          await uploadWithConcurrency({
            items: filesToUpload,
            maxConcurrent: 3,
            uploadFn: (file) => uploadSingleFile(file),
            onProgress: (current) => {
              setOverallProgress({
                current: preUploadedCount + current,
                total: matchedFiles.length,
              });
            },
          });

        const successCount = preUploadedCount + newSuccessCount;

        if (successCount > 0) {
          setSuccess(
            `Successfully uploaded ${newSuccessCount} final photo${
              newSuccessCount > 1 ? "s" : ""
            }!`
          );

          // Clear files after successful upload
          setTimeout(() => {
            setFiles([]);
            setMatchedPhotos(new Map());
            setProgress({});
            setOverallProgress({ current: 0, total: 0 });
            setSuccess("");
          }, 3000);
        }

        if (errorCount > 0) {
          setError(formatErrorMessage(errorCount));
        }
      } catch (err) {
        setError("Failed to upload photos. Please try again.");
        console.error("Upload error:", err);
      }

      setUploading(false);
    },
    [files, matchedPhotos, campusId, childId]
  );

  return {
    files,
    matchedPhotos,
    unmatchedFiles,
    uploading,
    progress,
    overallProgress,
    error,
    success,
    onDrop,
    matchFile,
    removeFile,
    uploadAll,
    setError,
    setSuccess,
  };
};
